import type { Agent } from "../../../../royalur/agent/Agent.js";
import { SimpleEngineProvider } from "../../../../royalur/engine/simple/SimpleEngineProvider.js";
import type { APIUser, APIGamePreferences } from "../../../api/api_schema.js";
import { GameMode } from "../../GameMode.js";
import type { LobbySettings } from "../../LobbySettings.js";
import { RemoteLobbySettings } from "../../RemoteLobbySettings.js";
import { LobbyID } from "../../../lobby/LobbyID.js";
import type { GamePlatformServices } from "../../GamePlatformServices.js";
import type { GameSource } from "./GameSource.js";
import { LocalComputerGameSource } from "./LocalComputerGameSource.js";
import { LocalGameSource } from "./LocalGameSource.js";
import { OnlineGameSource } from "./OnlineGameSource.js";


export interface CreateGameSourceConfig {
    user: APIUser | null;
    settings: LobbySettings;
    initialPreferences: APIGamePreferences;
    services: GamePlatformServices;
}


export function createGameSource({
    user,
    settings,
    initialPreferences,
    services,
}: CreateGameSourceConfig): GameSource {
    if (settings instanceof RemoteLobbySettings) {
        return new OnlineGameSource(
            services.clientControls,
            initialPreferences,
            services.analytics,
            services.gameAPI,
            settings,
            LobbyID.ofLiveLobby(settings.getLobbyID()),
            services.storage,
        );
    }
    if (!settings.isAvailable())
        throw new Error("Expected game settings to be available");

    const ruleProvider = new SimpleEngineProvider();
    const rules = ruleProvider.create(settings.getGameSettings());

    if (settings.getMode() === GameMode.COMPUTER) {
        const difficulty = settings.getBotType();
        const agent: Agent = services.botExecution.createAgent(difficulty, rules);
        return new LocalComputerGameSource(
            services.clientControls,
            initialPreferences,
            user,
            services.analytics,
            services.gameAPI,
            settings,
            rules,
            difficulty,
            agent,
        );
    }

    if (settings.getMode() === GameMode.LOCAL) {
        return new LocalGameSource(
            services.clientControls,
            initialPreferences,
            services.analytics,
            services.gameAPI,
            settings,
            rules,
        );
    }
    throw new Error(`Unknown game mode ${settings.getMode().getName()}`);
}
